import { HStack, VStack } from '@chakra-ui/react';
import { Button, Tag } from 'Atoms';
import { useEsrsAuditLogs } from 'Features/History/History.hooks';
import {
  HistorySideBarFiltering,
  HistorySidebar,
  HistorySidebarHeader,
} from 'Features/History/HistorySidebar';
import { Drawer } from 'Molecules';
import { filterOptions } from 'Molecules/HistorySidebarFilter';
import { Typography } from 'Tokens';
import { RemoveIcon } from 'Tokens/Icons/Function';
import { uniq } from 'lodash';
import { EsrsAssessmentQuery_, QuestionType_Enum_ } from 'models';
import { useMemo, useState } from 'react';

export const EsrsAssessmentHistory = ({
  esrsAssessment,
  isHistorySidebarOpen,
  setIsHistorySidebarOpen,
}: {
  esrsAssessment: EsrsAssessmentQuery_['esrsAssessment'];
  isHistorySidebarOpen: boolean;
  setIsHistorySidebarOpen: (open: boolean) => void;
}) => {
  const [searchValue, setSearchValue] = useState('');
  const shouldRenderContent = !!esrsAssessment && isHistorySidebarOpen;
  const [selected, setSelected] = useState<{ [x: string]: string[] }>({});

  const {
    auditLogs,
    context,
    loading: auditLogsLoading,
    refetch: refetchAuditLogs,
  } = useEsrsAuditLogs({ esrsAssessment });

  const selectedStandards = selected['Standard'] ?? [];
  const isStandardSelected = (stdTitle: string) =>
    selectedStandards.length === 0 || selectedStandards.includes(stdTitle);

  const filterOptions: filterOptions = {
    Standard: context?.materialStandards?.map((std) => std.standard.title).sort() ?? [],
    'Disclosure requirement': uniq(
      context?.materialStandards
        ?.filter((std) => isStandardSelected(std.standard.title))
        .flatMap((std) => std.materialMetrics.map((met) => met.metric.requirement.title)) ?? []
    ).sort(),
    'Business unit': uniq(context?.reportingUnits?.map((ru) => ru.name) ?? []),
    'Datapoint owner': uniq(
      context?.reportingUnits?.flatMap((ru) =>
        ru.answers
          .filter((ans) =>
            context?.materialStandards
              ?.filter((std) => isStandardSelected(std.standard.title))
              .some((std) =>
                std.materialMetrics.some((mm) => mm.metric.reference === ans.metricRef)
              )
          )
          .flatMap((ans) => ans.datapoints.map((dp) => dp.owner?.displayName ?? ''))
      ) ?? []
    )
      .filter(Boolean)
      .sort(),
    'Metric type': uniq(
      auditLogs
        .filter(
          (log) =>
            (selectedStandards.length === 0 ||
              selectedStandards.some((std) => log.metaInfo.standardName?.includes(std))) &&
            (!selected['Disclosure requirement'] ||
              selected['Disclosure requirement'].some((dr) => log.metaInfo.drName?.includes(dr)))
        )
        .map((log) => {
          const type = log.metaInfo.metricType?.toLowerCase();
          if (type === QuestionType_Enum_.Decimal_.toLowerCase()) return 'Decimal';
          if (type === QuestionType_Enum_.YesNo_.toLowerCase()) return 'Boolean';
          return 'Narrative';
        }) ?? []
    ),
  };

  const filteredLogs = useMemo(() => {
    if (!Object.keys(selected).length) return auditLogs;
    return auditLogs.filter((log) => {
      const conditions: [key: string, path: any][] = [
        ['Standard', log.metaInfo?.standardName],
        ['Disclosure requirement', log.metaInfo?.drName],
        ['Business unit', log.metaInfo?.reportingUnit],
        ['Metric type', log.metaInfo?.metricType],
        ['Datapoint owner', log.user?.displayName],
      ];
      return conditions.every(([key, value]) => {
        const selectedValues = selected[key];
        return !value || !selectedValues || selectedValues.some((val) => value.includes(val));
      });
    });
  }, [auditLogs, selected]);

  const handleRemoveFilter = (label: string, option: string) => {
    const currentOptions = selected[label] || [];
    const isSelected = currentOptions.includes(option);
    const updatedOptions = isSelected
      ? currentOptions.filter((opt) => opt !== option)
      : [...currentOptions, option];
    const updatedSelected = {
      ...selected,
      ...(updatedOptions.length > 0 ? { [label]: updatedOptions } : {}),
    };

    if (updatedOptions.length === 0) {
      delete updatedSelected[label];
    }
    setSelected(updatedSelected);
  };

  return (
    <Drawer
      onClose={() => {
        setIsHistorySidebarOpen(false);
      }}
      isOpen={shouldRenderContent}
      width="400px"
      hasOverlay={false}
      returnFocusOnClose={false}
      header={
        shouldRenderContent ? (
          <VStack width="100%" paddingBottom="0px" alignItems="start">
            <HistorySidebarHeader />
            <HistorySideBarFiltering
              title={String(esrsAssessment?.reportingYear) ?? ''}
              refetchAuditLogs={refetchAuditLogs}
              searchValue={searchValue}
              setSearchValue={setSearchValue}
              filterOptions={filterOptions}
              selected={selected}
              setSelected={setSelected}
            />
            {!!Object.values(selected).length && (
              <HStack wrap="wrap" gap="8px" w="100%">
                <Typography variant="bodyStrong" width="fit-content">
                  Filters:
                </Typography>
                {Object.values(selected).map((label) =>
                  label.map((option) => (
                    <Tag
                      title={option}
                      size="sm"
                      truncatable
                      rightIcon={<RemoveIcon color="text.muted" />}
                      onRemove={() =>
                        handleRemoveFilter(
                          Object.keys(selected).find((key) => selected[key] === label) ?? '',
                          option
                        )
                      }
                      maxW="48%"
                    />
                  ))
                )}
                {(Object.values(selected).length >= 2 ||
                  Object.values(selected).some((val) => val.length >= 2)) && (
                  <Button
                    size="sm"
                    variant="ghost"
                    leftIcon={<RemoveIcon color="text.muted" />}
                    onClick={() => setSelected({})}
                  >
                    <Typography variant="bodyStrong" color="text.muted">
                      Clear all
                    </Typography>
                  </Button>
                )}
              </HStack>
            )}
          </VStack>
        ) : undefined
      }
      headerProps={{
        paddingBottom: '0px',
        borderLeft: '1px solid',
        borderColor: 'border.decorative',
      }}
      bodyProps={{
        padding: '0px',
        borderLeft: '1px solid',
        borderColor: 'border.decorative',
      }}
    >
      {shouldRenderContent && (
        <HistorySidebar
          key="history-drawer"
          auditLogs={filteredLogs}
          loading={auditLogsLoading}
          searchValue={searchValue}
        />
      )}
      <></>
    </Drawer>
  );
};
